import React, { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import ButtonDropdown, { ButtonDropdownProps } from '@cloudscape-design/components/button-dropdown';
import { Pagination, Spinner, Table, TableProps } from '@cloudscape-design/components';
import PropertyFilter from '@cloudscape-design/components/property-filter';
import Alert from '@cloudscape-design/components/alert';
import { getTextFilterCounterText } from '../../../utils/text-filter';
import { useLocalStorage } from '../../../components/use-local-storage';
import { useColumnWidths } from '../../../components/table/use-column-width';
import { mutate } from 'swr';
import { ClosuresTableProps, ClosureSummary } from '../../../types/closures';
import { useSplitPanelControl } from '../../../utils/hooks/splitPanelContext';
import { useNavigate } from 'react-router-dom';
import { useTableConfig } from './table.config';

import { TableEmptyStateProps } from '../../../components/table/commons';
import ClosuresSplitPanelDetails from './closuresSplitPanelDetails';
import { useApiWithFlash } from '../../../utils/hooks/useApiWithFlashInstance';
import { useAppSession } from '../../../utils/hooks/sessionContext';
import { Actions, ResourceTypes } from '../../../types/rolePermissions';

import { ProtectedUtilityCreate, useProtectUtility } from '../../../components/protectedUtility';
import { useCatoCollection } from '../../../utils/hooks/useCatoCollection';
import { filteringProperties } from './filtering_poperties';

export default function ClosuresTable({ isLoading, closures = [], error }: Readonly<ClosuresTableProps>) {
  const protectUtility = useProtectUtility();

  const { columns, defaultPreferences, Preferences } = useTableConfig();

  // State
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<ClosureSummary[]>([]);
  const [columnDefinitions, saveWidths] = useColumnWidths('Cato-Closures-TableEditable-Widths', columns);

  const [preferences, setPreferences] = useLocalStorage('Cato-Closures-TableEditable-Preferences', defaultPreferences);

  // Hooks
  const { setSplitPanelOpen, setSplitPanelContent } = useSplitPanelControl();
  const navigate = useNavigate();
  const { handleApiWithFlash } = useApiWithFlash();
  const appSession = useAppSession();
  const instanceArn = appSession?.instanceSelected?.instanceArn;

  const tableEmptyStateProps: TableEmptyStateProps = {
    resourceName: 'Closures',
    resourceType: ResourceTypes.Closures,
    urlPath: 'closures',
  };

  const { items, filteredItemsCount, collectionProps, propertyFilterProps, paginationProps } = useCatoCollection(closures, {tableEmptyStateProps, pageSize: preferences.pageSize, filteringProperties} );

  const onRefresh = async () => {
    setRefreshLoading(true);
    const endpoint = '/closures';
    const key = instanceArn ? [endpoint, instanceArn] : [endpoint];
    await mutate(key);
    setRefreshLoading(false);
  };

  const handleUtilityItemClick = async (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
    const details = event.detail;
    if (details.id === 'copy') {
      navigate(`/closures/copy/${selectedItems[0].feature}`);
    }
    if (details.id === 'edit') {
      navigate(`/closures/edit/${selectedItems[0].feature}`);
    }
    if (details.id === 'delete') {
      await handleApiWithFlash(`/closures/${selectedItems[0].feature}`, 'DELETE', {
        successMessage: `Successfully deleted closure group  ${selectedItems[0].feature}`,
        errorMessage: `Error deleting closure group ${selectedItems[0].feature}`,
        deleteMessage: `Are you sure you want to delete closure group ${selectedItems[0].feature}`,
      });

      setSelectedItems([]);
      setSplitPanelOpen(false);
      setSplitPanelContent(null);
      onRefresh();
    }
  };

  const refreshButtonProps = { onClick: onRefresh };

  function onSelectionChange(data: { detail: any }) {
    setSplitPanelContent(<ClosuresSplitPanelDetails selectedItems={data.detail[0]} />);
    setSplitPanelOpen(true);
  }

  const functions = [
    { text: 'Copy', action: Actions.Create },
    { text: 'Edit', action: Actions.Update },
    { text: 'Delete', action: Actions.Delete },
  ];

  const dropDownButtonMenuProps: ButtonDropdownProps = {
    disabled: !selectedItems || selectedItems.length === 0,
    onItemClick: handleUtilityItemClick,
    loadingText: 'loading',
    items: functions
      .filter((f) => protectUtility({ resourceType: ResourceTypes.Closures, resourceAction: f.action }))
      .map((f) => ({
        text: f.text,
        id: f.text.toLowerCase(),
        disabled: false,
      })),
  };

  const header = (
    <Header
      variant='awsui-h1-sticky'
      actions={
        <SpaceBetween
          direction='horizontal'
          size='xs'
        >
          <Button
            iconName='refresh'
            ariaLabel='Refresh'
            {...refreshButtonProps}
          />
          {!!dropDownButtonMenuProps.items.length && <ButtonDropdown {...{ ...dropDownButtonMenuProps }}>Actions</ButtonDropdown>}
          <ProtectedUtilityCreate resourceType={ResourceTypes.Closures}>
            <Button
              variant='primary'
              onClick={() => navigate('/closures/create')}
            >
              Create closure group
            </Button>
          </ProtectedUtilityCreate>
        </SpaceBetween>
      }
    >
      Closure Groups {closures && !isLoading ? `(${closures.length})` : <Spinner />}
    </Header>
  );

  const filter = (
    <PropertyFilter
      {...propertyFilterProps}
      countText={getTextFilterCounterText(filteredItemsCount)}
      expandToViewport={true}
      filteringAriaLabel={'Filter closure groups'}
      filteringPlaceholder={'Filter closure groups'}
    />
  );

  const closureTableProps: TableProps = {
    ...collectionProps,
    stickyHeader: true,
    resizableColumns: true,
    onColumnWidthsChange: saveWidths,
    onSelectionChange: ({ detail: { selectedItems } }) => {
      onSelectionChange({ detail: selectedItems });
      if (selectedItems && selectedItems.length > 0) {
        const selectedItem: ClosureSummary = selectedItems[0];
        setSelectedItems(() => [selectedItem]);
      }
    },
    selectedItems,
    columnDefinitions,
    items,
    // submitEdit:handleSubmit,
    trackBy: 'feature',
    loadingText: 'Loading closure groups',
    columnDisplay: preferences.contentDisplay,
    wrapLines: preferences.wrapLines,
    stripedRows: preferences.stripedRows,
    stickyColumns: preferences.stickyColumns,
    contentDensity: preferences.contentDensity as 'compact' | 'comfortable',
    selectionType: 'single',
    loading: isLoading || !closures || refreshLoading,
    header,
    filter,
    pagination: <Pagination {...paginationProps} />,
    preferences: <Preferences {...{ preferences, setPreferences }} />,
  };

  return (
    <>
      <Table {...{ ...closureTableProps }} />
      {error && (
        <Alert
          statusIconAriaLabel='Error'
          type='error'
          header='Error getting data'
        >
          Refresh the page to try again.
        </Alert>
      )}
    </>
  );
}
